import { z } from '@faceup/validation'
import { DisclosureFormStatus, FormItemType } from '../enums'
import { Language } from '../localization/enums'
import {
  dateFormItemSchema,
  emailFormItemSchema,
  multilineTextFormItemSchema,
  multiSelectFormItemSchema,
  numberFormItemSchema,
  phoneNumberFormItemSchema,
  selectFormItemSchema,
  simpleTextFormItemSchema,
  yesNoFormItemSchema,
} from './abstractFormItemSchemas'
import { tiptapContentSchema } from './tiptapContentSchema'

/**
 * Form item types allowed in disclosure forms.
 */
export const allowedDisclosureFormItemTypes = [
  FormItemType.SimpleText,
  FormItemType.MultilineText,
  FormItemType.Select,
  FormItemType.MultiSelect,
  FormItemType.Date,
  FormItemType.Number,
  FormItemType.Email,
  FormItemType.PhoneNumber,
  FormItemType.YesNo,
] as const

export const publicDisclosureFormItemSchema = z.discriminatedUnion('type', [
  simpleTextFormItemSchema,
  dateFormItemSchema,
  numberFormItemSchema,
  emailFormItemSchema,
  phoneNumberFormItemSchema,
  multilineTextFormItemSchema,
  selectFormItemSchema,
  multiSelectFormItemSchema,
  yesNoFormItemSchema,
])

export const publicDisclosureFormSchema = z.object({
  id: z.uuidv7(),
  formId: z.uuidv7(),
  name: z.string().nullable(),
  description: tiptapContentSchema.nullable(),
  status: z.enum(DisclosureFormStatus),
  dueDate: z.iso.date().nullable(),
  nameFormItemId: z.string(),
  emailFormItemId: z.string(),
  language: z.enum(Language),
  defaultLanguage: z.enum(Language),
  formItems: z.array(publicDisclosureFormItemSchema),
})

/**
 * A deleted disclosure form keeps its shortlink reserved forever, so its link still resolves —
 * there is just no form left behind it, and the respondent gets the closed page (which needs no
 * form data). A shortlink that was never reserved is a genuine 404 and is not represented here.
 */
export const publicDisclosureFormResultSchema = z.discriminatedUnion('isDeleted', [
  z.object({ isDeleted: z.literal(false), form: publicDisclosureFormSchema }),
  z.object({ isDeleted: z.literal(true) }),
])

export type PublicDisclosureFormItem = z.infer<typeof publicDisclosureFormItemSchema>
export type PublicDisclosureForm = z.infer<typeof publicDisclosureFormSchema>
export type PublicDisclosureFormResult = z.infer<typeof publicDisclosureFormResultSchema>
